import { useState, useEffect, useRef } from "react";
import api, { BACKEND_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { X, Plus, Trash2, Loader2, Pencil, ImageIcon, Upload, ChevronUp, ChevronDown } from "lucide-react";
import { ALLERGENS } from "@/lib/allergens";
import { AllergenPicker, UNIT_OPTIONS, normUnit } from "./adminShared";

/* ============ MENU MANAGEMENT ============ */
export default function MenuManagement({ categories, products, onRefresh, canEdit }) {
  const [selectedCat, setSelectedCat] = useState(null);
  const [catDialog, setCatDialog] = useState(false);
  const [prodDialog, setProdDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [catForm, setCatForm] = useState({ name: "", description: "", image_url: "", sort_order: 0 });
  const [prodForm, setProdForm] = useState({ name: "", description: "", base_price: "", image_url: "", category_id: "", ingredients: [], modifiers: [], allergens: [], option_groups: [], recipe: [] });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [newIng, setNewIng] = useState("");
  const [newMod, setNewMod] = useState({ name: "", price: "", max_quantity: "3" });
  const [inventoryItems, setInventoryItems] = useState([]);
  const catFileRef = useRef(null);
  const prodFileRef = useRef(null);

  // Load Lager item names so the recipe editor can autocomplete existing items.
  useEffect(() => {
    api.get("/admin/inventory/config")
      .then(res => setInventoryItems(res.data?.inventory_items || []))
      .catch(() => {});
  }, [prodDialog]);

  const handleImageUpload = async (file, target) => {
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await api.post("/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
      const url = res.data.url.startsWith("http") ? res.data.url : `${BACKEND_URL}${res.data.url}`;
      if (target === "category") {
        setCatForm(f => ({ ...f, image_url: url }));
      } else {
        setProdForm(f => ({ ...f, image_url: url }));
      }
      toast.success("Bild uppladdad");
    } catch (e) {
      toast.error("Uppladdning misslyckades");
    } finally {
      setUploading(false);
    }
  };

  const filteredProducts = selectedCat ? products.filter(p => p.category_id === selectedCat && p.active !== false) : products.filter(p => p.active !== false);

  const openEditCategory = (cat) => {
    setEditingCategory(cat);
    setCatForm({ name: cat.name, description: cat.description || "", image_url: cat.image_url || "", sort_order: cat.sort_order || 0 });
    setCatDialog(true);
  };

  const openNewCategory = () => {
    setEditingCategory(null);
    setCatForm({ name: "", description: "", image_url: "", sort_order: 0 });
    setCatDialog(true);
  };

  const moveCategory = async (orderedIds, idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= orderedIds.length) return;
    const arr = [...orderedIds];
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    try {
      await api.put("/admin/categories/reorder", { ids: arr });
      onRefresh();
    } catch (e) {
      toast.error("Kunde inte ändra ordning");
    }
  };

  const handleSaveCategory = async () => {
    if (!catForm.name) { toast.error("Namn krävs"); return; }
    setSaving(true);
    try {
      if (editingCategory) {
        await api.put(`/admin/categories/${editingCategory.id}`, catForm);
        toast.success("Kategori uppdaterad");
      } else {
        await api.post("/admin/categories", catForm);
        toast.success("Kategori skapad");
      }
      setCatDialog(false);
      setEditingCategory(null);
      setCatForm({ name: "", description: "", image_url: "", sort_order: 0 });
      onRefresh();
    } catch (e) { toast.error("Misslyckades"); } finally { setSaving(false); }
  };

  const handleDeleteCategory = async (id) => {
    try { await api.delete(`/admin/categories/${id}`); toast.success("Raderad"); onRefresh(); } catch (e) { toast.error("Misslyckades"); }
  };

  const openEditProduct = (prod) => {
    setEditingProduct(prod);
    setProdForm({
      name: prod.name,
      description: prod.description || "",
      base_price: String(prod.base_price || ""),
      image_url: prod.image_url || "",
      category_id: prod.category_id || "",
      ingredients: (prod.ingredients || []).map(i => ({ name: i.name, removable: i.removable !== false, included_by_default: i.included_by_default !== false })),
      modifiers: (prod.modifiers || []).map(m => ({ name: m.name, price: String(m.price || ""), max_quantity: String(m.max_quantity || 3), allergens: m.allergens || [] })),
      allergens: Array.isArray(prod.allergens) ? prod.allergens : [],
      option_groups: (prod.option_groups || []).map(g => ({
        name: g.name,
        options: (g.options || []).map(o => ({ name: o.name, price: String(o.price || ""), allergens: o.allergens || [] })),
      })),
      recipe: (prod.recipe || []).map(r => ({ item_id: r.item_id || null, name: r.name, unit: r.unit || "st", quantity: String(r.quantity ?? "") })),
      tax_nature: prod.tax_nature || "unresolved",
    });
    setProdDialog(true);
  };

  const openNewProduct = () => {
    setEditingProduct(null);
    setProdForm({ name: "", description: "", base_price: "", image_url: "", category_id: selectedCat || categories[0]?.id || "", ingredients: [], modifiers: [], allergens: [], option_groups: [], recipe: [] });
    setProdDialog(true);
  };

  const handleSaveProduct = async () => {
    if (!prodForm.category_id || !prodForm.name || !prodForm.base_price) { toast.error("Fyll i obligatoriska fält"); return; }
    setSaving(true);
    try {
      const payload = {
        ...prodForm,
        base_price: parseFloat(prodForm.base_price),
        ingredients: prodForm.ingredients.map(ing => ({ name: ing.name, removable: ing.removable !== false, included_by_default: ing.included_by_default !== false })),
        modifiers: prodForm.modifiers.map(mod => ({ name: mod.name, price: parseFloat(mod.price), max_quantity: parseInt(mod.max_quantity), allergens: mod.allergens || [] })),
        allergens: prodForm.allergens || [],
        option_groups: (prodForm.option_groups || [])
          .filter(g => g.name && g.name.trim() && (g.options || []).some(o => o.name && o.name.trim()))
          .map(g => ({
            name: g.name.trim(),
            options: (g.options || [])
              .filter(o => o.name && o.name.trim())
              .map(o => ({ name: o.name.trim(), price: parseFloat(o.price) || 0, allergens: o.allergens || [] })),
          })),
        recipe: (prodForm.recipe || [])
          .filter(r => r.name && r.name.trim())
          .map(r => ({ item_id: r.item_id || null, name: r.name.trim(), unit: (r.unit || "st").trim() || "st", quantity: parseFloat(r.quantity) || 0 })),
      };
      // Tax nature travels only when the user actively changed it — the form
      // never silently submits a classification on behalf of the user.
      if ((editingProduct?.tax_nature || "unresolved") === (prodForm.tax_nature || "unresolved")) {
        delete payload.tax_nature;
      }
      if (editingProduct) {
        await api.put(`/admin/products/${editingProduct.id}`, payload);
        toast.success("Produkt uppdaterad");
      } else {
        await api.post("/admin/products", payload);
        toast.success("Produkt skapad");
      }
      setProdDialog(false);
      setEditingProduct(null);
      setProdForm({ name: "", description: "", base_price: "", image_url: "", category_id: "", ingredients: [], modifiers: [], allergens: [], option_groups: [], recipe: [] });
      onRefresh();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Kunde inte spara produkten — kontrollera fälten (pris på tillval får inte vara tomt).");
    } finally { setSaving(false); }
  };

  const handleDeleteProduct = async (id) => {
    try { await api.delete(`/admin/products/${id}`); toast.success("Raderad"); onRefresh(); } catch (e) { toast.error("Misslyckades"); }
  };

  const handleToggleAvailability = async (prod) => {
    try {
      const available = !!prod.out_of_stock;
      await api.put(`/admin/products/${prod.id}/availability`, {
        available,
        reason: available ? "" : "Tillfälligt slut",
      });
      toast.success(available ? "Product reopened" : "Product marked as tillfälligt slut");
      onRefresh();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to update availability");
    }
  };

  const addIngredient = () => {
    if (!newIng.trim()) return;
    setProdForm(f => ({ ...f, ingredients: [...f.ingredients, { name: newIng.trim() }] }));
    setNewIng("");
  };

  const addModifier = () => {
    if (!newMod.name || !newMod.price) return;
    setProdForm(f => ({ ...f, modifiers: [...f.modifiers, { ...newMod }] }));
    setNewMod({ name: "", price: "", max_quantity: "3" });
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-5">
      <div className="lg:col-span-1">
        <div className="bg-[#121212] rounded-xl border border-[#27272A] p-4 sticky top-20">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-playfair text-sm font-semibold text-[#EDEDED]">Kategorier</h3>
            {canEdit && (
              <Button size="sm" variant="ghost" onClick={openNewCategory} className="text-[#D4B483] h-7 px-2 text-xs" data-testid="add-category-btn">
                <Plus className="w-3 h-3 mr-1" /> Add
              </Button>
            )}
          </div>
          <div className="space-y-1">
            <button onClick={() => setSelectedCat(null)}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${!selectedCat ? "bg-[#D4B483]/15 text-[#D4B483]" : "text-[#E8E8EA] hover:text-[#EDEDED] hover:bg-[#1A1A1A]"}`}>
              All ({products.filter(p => p.active !== false).length})
            </button>
            {(() => {
              const activeCats = categories.filter(c => c.active !== false);
              const orderedIds = activeCats.map(c => c.id);
              return activeCats.map((cat, idx) => (
              <div key={cat.id} className="flex items-center group">
                {canEdit && (
                  <div className="flex flex-col mr-0.5">
                    <button onClick={() => moveCategory(orderedIds, idx, -1)} disabled={idx === 0}
                      className="p-0.5 text-[#B4B4BC] hover:text-[#D4B483] disabled:opacity-20 disabled:hover:text-[#B4B4BC]" title="Flytta upp">
                      <ChevronUp className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => moveCategory(orderedIds, idx, 1)} disabled={idx === activeCats.length - 1}
                      className="p-0.5 text-[#B4B4BC] hover:text-[#D4B483] disabled:opacity-20 disabled:hover:text-[#B4B4BC]" title="Flytta ner">
                      <ChevronDown className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
                <button onClick={() => setSelectedCat(cat.id)}
                  className={`flex-1 text-left px-3 py-2 rounded-lg text-sm transition-colors flex items-center gap-2 ${selectedCat === cat.id ? "bg-[#D4B483]/15 text-[#D4B483]" : "text-[#E8E8EA] hover:text-[#EDEDED] hover:bg-[#1A1A1A]"}`}
                  data-testid={`cat-select-${cat.id}`}>
                  {cat.image_url ? (
                    <img src={cat.image_url} alt="" className="w-6 h-6 rounded object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-6 h-6 rounded bg-[#27272A] flex items-center justify-center flex-shrink-0">
                      <ImageIcon className="w-3 h-3 text-[#E8E8EA]" />
                    </div>
                  )}
                  <span>{cat.name} ({products.filter(p => p.category_id === cat.id && p.active !== false).length})</span>
                </button>
                {canEdit && (
                  <>
                    <button onClick={() => openEditCategory(cat)} className="opacity-0 group-hover:opacity-100 p-1 text-[#E8E8EA] hover:text-[#D4B483] transition-opacity" title="Redigera">
                      <Pencil className="w-3 h-3" />
                    </button>
                    <button onClick={() => handleDeleteCategory(cat.id)} className="opacity-0 group-hover:opacity-100 p-1 text-[#EF4444] transition-opacity" data-testid={`cat-delete-${cat.id}`}>
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </>
                )}
              </div>
              ));
            })()}
          </div>
        </div>
      </div>

      <div className="lg:col-span-3">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-playfair text-lg font-semibold text-[#EDEDED]">Products ({filteredProducts.length})</h3>
          {canEdit && (
            <div className="flex gap-2">
              {products.some(p => !p.tax_nature || p.tax_nature === "unresolved") && (
                <Button size="sm" variant="outline"
                  onClick={async () => {
                    if (!window.confirm("Klassificera alla oklassificerade produkter som MAT via godkänd produktmall? Åtgärden loggas i händelseloggen och kan granskas per produkt.")) return;
                    try {
                      const res = await api.post("/admin/products/classify-by-template", { template: "food_product_template" });
                      toast.success(`${res.data.classified} produkter klassificerade via mall`);
                      onRefresh();
                    } catch (e) { toast.error(e.response?.data?.detail || "Kunde inte klassificera"); }
                  }}
                  className="border-[#F59E0B]/50 text-[#F59E0B] hover:bg-[#F59E0B]/10 text-xs">
                  Momsklassificera via mall
                </Button>
              )}
              <Button size="sm" onClick={openNewProduct}
                className="bg-[#D4B483] text-[#0A0A0A] hover:bg-[#E5CFA5] font-semibold" data-testid="add-product-btn">
                <Plus className="w-4 h-4 mr-1.5" /> Lägg till produkt
              </Button>
            </div>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {filteredProducts.map(prod => (
            <div key={prod.id} className="bg-[#121212] rounded-xl border border-[#27272A] overflow-hidden group hover:border-[#3F3F46] transition-colors" data-testid={`admin-product-${prod.id}`}>
              <div className="relative">
                {prod.image_url && <img src={prod.image_url} alt={prod.name} className="w-full h-36 object-cover" />}
                {canEdit && (
                  <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => openEditProduct(prod)}
                      className="w-7 h-7 rounded-full bg-[#0A0A0A]/80 backdrop-blur flex items-center justify-center text-[#D4B483] hover:bg-[#D4B483] hover:text-[#0A0A0A] transition-colors" title="Redigera">
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => handleDeleteProduct(prod.id)}
                      className="w-7 h-7 rounded-full bg-[#0A0A0A]/80 backdrop-blur flex items-center justify-center text-[#EF4444] hover:bg-[#EF4444] hover:text-white transition-colors" title="Radera">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
                {!prod.image_url && (
                  <div className="w-full h-36 bg-[#0A0A0A] flex items-center justify-center">
                    <ImageIcon className="w-8 h-8 text-[#27272A]" />
                  </div>
                )}
              </div>
              <div className="p-4">
                <div className="flex justify-between items-start mb-1">
                  <h4 className="font-semibold text-[#EDEDED] text-sm">{prod.name}</h4>
                  <span className="text-[#D4B483] text-sm font-bold">{prod.base_price?.toFixed(0)} kr</span>
                </div>
                {prod.out_of_stock && (
                  <div className="inline-flex items-center bg-[#DC2626]/15 border border-[#DC2626]/40 text-[#FCA5A5] text-xs font-semibold px-2 py-1 rounded-md mb-1.5">
                    Tillfälligt slut
                  </div>
                )}
                <p className="text-xs text-[#E8E8EA] line-clamp-2">{prod.description}</p>
                {prod.ingredients?.length > 0 && <p className="text-xs text-[#B4B4BC] mt-1.5">Ingredients: {prod.ingredients.map(i => i.name).join(", ")}</p>}
                {prod.modifiers?.length > 0 && <p className="text-xs text-[#D4B483]/60 mt-0.5">Add-ons: {prod.modifiers.map(m => `${m.name} +${m.price}kr`).join(", ")}</p>}
                <div className="mt-3">
                  <Button
                    size="sm"
                    onClick={() => handleToggleAvailability(prod)}
                    className={`${prod.out_of_stock ? "bg-[#10B981] text-[#0A0A0A] hover:bg-[#34D399]" : "bg-[#EF4444] text-white hover:bg-[#F87171]"}`}
                    data-testid={`toggle-stock-${prod.id}`}
                  >
                    {prod.out_of_stock ? "Mark as available" : "Mark tillfälligt slut"}
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={catDialog} onOpenChange={(v) => { setCatDialog(v); if (!v) setEditingCategory(null); }}>
        <DialogContent className="bg-[#121212] border-[#27272A]">
          <DialogHeader><DialogTitle className="text-[#EDEDED] font-playfair">{editingCategory ? "Edit Category" : "Add Category"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input value={catForm.name} onChange={e => setCatForm(f => ({ ...f, name: e.target.value }))} placeholder="Kategorinamn" className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED] h-11" data-testid="cat-name-input" />
            <Input value={catForm.description} onChange={e => setCatForm(f => ({ ...f, description: e.target.value }))} placeholder="Beskrivning" className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED]" />
            <div>
              <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Bild</Label>
              <div className="space-y-2">
                <input type="file" accept="image/*" ref={catFileRef} className="hidden"
                  onChange={e => { handleImageUpload(e.target.files[0], "category"); e.target.value = ""; }} />
                <button type="button" onClick={() => catFileRef.current?.click()} disabled={uploading}
                  className="w-full h-24 rounded-lg border-2 border-dashed border-[#27272A] hover:border-[#D4B483]/50 bg-[#0A0A0A] flex flex-col items-center justify-center gap-1.5 transition-colors cursor-pointer disabled:opacity-50">
                  {uploading ? <Loader2 className="w-5 h-5 text-[#D4B483] animate-spin" /> : <Upload className="w-5 h-5 text-[#E8E8EA]" />}
                  <span className="text-xs text-[#E8E8EA]">{uploading ? "Uploading..." : "Click to upload image"}</span>
                </button>
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-px bg-[#27272A]" />
                  <span className="text-xs text-[#B4B4BC]">eller klistra in URL</span>
                  <div className="flex-1 h-px bg-[#27272A]" />
                </div>
                <Input value={catForm.image_url} onChange={e => setCatForm(f => ({ ...f, image_url: e.target.value }))} placeholder="https://..." className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED] text-xs h-9" />
                {catForm.image_url && (
                  <div className="rounded-lg overflow-hidden border border-[#27272A] relative group">
                    <img src={catForm.image_url} alt="Preview" className="w-full h-32 object-cover" onError={e => e.target.style.display='none'} />
                    <button type="button" onClick={() => setCatForm(f => ({ ...f, image_url: "" }))}
                      className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-[#0A0A0A]/80 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-[#EF4444] hover:bg-[#EF4444] hover:text-white">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div>
              <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Sorteringsordning</Label>
              <Input type="number" value={catForm.sort_order} onChange={e => setCatForm(f => ({ ...f, sort_order: parseInt(e.target.value) || 0 }))} className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED] w-24" />
            </div>
            <Button onClick={handleSaveCategory} disabled={saving} className="w-full bg-[#D4B483] text-[#0A0A0A] hover:bg-[#E5CFA5] h-11 font-semibold" data-testid="save-cat-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingCategory ? "Update Category" : "Save Category")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={prodDialog} onOpenChange={(v) => { setProdDialog(v); if (!v) setEditingProduct(null); }}>
        <DialogContent className="bg-[#121212] border-[#27272A] max-w-lg max-h-[85vh]">
          <DialogHeader><DialogTitle className="text-[#EDEDED] font-playfair">{editingProduct ? "Edit Product" : "Lägg till produkt"}</DialogTitle></DialogHeader>
          <ScrollArea className="max-h-[65vh] pr-2">
            <div className="space-y-4">
              <div>
                <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Category *</Label>
                <select value={prodForm.category_id} onChange={e => setProdForm(f => ({ ...f, category_id: e.target.value }))}
                  className="w-full h-11 rounded-lg border border-[#27272A] bg-[#0A0A0A] text-[#EDEDED] px-3 text-sm" data-testid="prod-category-select">
                  <option value="">Välj kategori</option>
                  {categories.filter(c => c.active !== false).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Name *</Label>
                <Input value={prodForm.name} onChange={e => setProdForm(f => ({ ...f, name: e.target.value }))} placeholder="Produktnamn" className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED] h-11" data-testid="prod-name-input" />
              </div>
              <div>
                <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Beskrivning</Label>
                <Textarea value={prodForm.description} onChange={e => setProdForm(f => ({ ...f, description: e.target.value }))} placeholder="Kort beskrivning" className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED] resize-none h-20" />
              </div>
              <div>
                <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Price (kr) *</Label>
                <Input type="number" value={prodForm.base_price} onChange={e => setProdForm(f => ({ ...f, base_price: e.target.value }))} placeholder="149" className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED] h-11" data-testid="prod-price-input" />
              </div>
              <div>
                <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Momsnatur (finans — endast admin kan ändra)</Label>
                <select value={prodForm.tax_nature || "unresolved"}
                  onChange={e => setProdForm(f => ({ ...f, tax_nature: e.target.value }))}
                  className={`w-full bg-[#0A0A0A] border rounded-md h-11 text-sm px-2 ${(prodForm.tax_nature || "unresolved") === "unresolved" ? "border-[#F59E0B]/60 text-[#F59E0B]" : "border-[#27272A] text-[#EDEDED]"}`}>
                  <option value="unresolved">Oklassificerad (blockerar fakturering)</option>
                  <option value="food">Mat (livsmedel)</option>
                  <option value="alcohol">Alkohol</option>
                  <option value="service">Tjänst</option>
                  <option value="other">Övrigt (standardmoms)</option>
                </select>
              </div>
              <div>
                <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Bild</Label>
                <div className="space-y-2">
                  <input type="file" accept="image/*" ref={prodFileRef} className="hidden"
                    onChange={e => { handleImageUpload(e.target.files[0], "product"); e.target.value = ""; }} />
                  <button type="button" onClick={() => prodFileRef.current?.click()} disabled={uploading}
                    className="w-full h-24 rounded-lg border-2 border-dashed border-[#27272A] hover:border-[#D4B483]/50 bg-[#0A0A0A] flex flex-col items-center justify-center gap-1.5 transition-colors cursor-pointer disabled:opacity-50">
                    {uploading ? <Loader2 className="w-5 h-5 text-[#D4B483] animate-spin" /> : <Upload className="w-5 h-5 text-[#E8E8EA]" />}
                    <span className="text-xs text-[#E8E8EA]">{uploading ? "Uploading..." : "Click to upload image"}</span>
                  </button>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-px bg-[#27272A]" />
                    <span className="text-xs text-[#B4B4BC]">eller klistra in URL</span>
                    <div className="flex-1 h-px bg-[#27272A]" />
                  </div>
                  <Input value={prodForm.image_url} onChange={e => setProdForm(f => ({ ...f, image_url: e.target.value }))} placeholder="/assets/plates/..." className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED] text-xs h-9" />
                  {prodForm.image_url && (
                    <div className="rounded-lg overflow-hidden border border-[#27272A] relative group">
                      <img src={prodForm.image_url} alt="Preview" className="w-full h-36 object-cover" onError={e => e.target.style.display='none'} />
                      <button type="button" onClick={() => setProdForm(f => ({ ...f, image_url: "" }))}
                        className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-[#0A0A0A]/80 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-[#EF4444] hover:bg-[#EF4444] hover:text-white">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Ingredienser</Label>
                <div className="flex gap-2 mb-2">
                  <Input value={newIng} onChange={e => setNewIng(e.target.value)} placeholder="t.ex. Sallad"
                    className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED] flex-1" onKeyDown={e => e.key === "Enter" && addIngredient()} data-testid="ing-name-input" />
                  <Button type="button" onClick={addIngredient} size="sm" className="bg-[#27272A] text-[#EDEDED] hover:bg-[#3F3F46]" data-testid="add-ing-btn">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {prodForm.ingredients.map((ing, i) => (
                    <span key={i} className="bg-[#D4B483]/10 text-[#D4B483] text-xs px-2.5 py-1 rounded-full flex items-center gap-1">
                      {ing.name}
                      <button onClick={() => setProdForm(f => ({ ...f, ingredients: f.ingredients.filter((_, idx) => idx !== i) }))} className="hover:text-[#EF4444]">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-[#E8E8EA] text-xs mb-1.5 block">
                  Allergener <span className="text-[#A8ABB2] normal-case">(visas för kunden – lagkrav)</span>
                </Label>
                <div className="flex flex-wrap gap-1.5">
                  {ALLERGENS.map((a) => {
                    const selected = (prodForm.allergens || []).includes(a.key);
                    return (
                      <button
                        type="button"
                        key={a.key}
                        onClick={() => setProdForm(f => ({
                          ...f,
                          allergens: selected
                            ? (f.allergens || []).filter(k => k !== a.key)
                            : [...(f.allergens || []), a.key],
                        }))}
                        className={`text-xs px-2.5 py-1.5 rounded-full border transition-all ${
                          selected
                            ? "bg-[#D4B483]/15 border-[#D4B483]/50 text-[#D4B483]"
                            : "bg-[#0A0A0A] border-[#27272A] text-[#B4B4BC] hover:border-[#3F3F46] hover:text-[#D4D4D8]"
                        }`}
                        data-testid={`allergen-${a.key}`}
                      >
                        {a.emoji} {a.sv}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <Label className="text-[#E8E8EA] text-xs mb-1.5 block">
                  Valbara alternativ <span className="text-[#A8ABB2] normal-case">(kunden väljer ett – t.ex. Tillagning: Friterad/Kokt)</span>
                </Label>
                <div className="space-y-3">
                  {(prodForm.option_groups || []).map((group, gi) => (
                    <div key={gi} className="bg-[#0A0A0A] border border-[#27272A] rounded-lg p-3">
                      <div className="flex gap-2 mb-2">
                        <Input
                          value={group.name}
                          onChange={e => setProdForm(f => { const og = [...f.option_groups]; og[gi] = { ...og[gi], name: e.target.value }; return { ...f, option_groups: og }; })}
                          placeholder="Gruppnamn (t.ex. Tillagning)"
                          className="bg-[#121212] border-[#27272A] text-[#EDEDED] flex-1 h-9 text-sm"
                          data-testid={`optgroup-name-${gi}`}
                        />
                        <button type="button" onClick={() => setProdForm(f => ({ ...f, option_groups: f.option_groups.filter((_, i) => i !== gi) }))} className="text-[#B4B4BC] hover:text-[#EF4444] px-1.5">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="space-y-2.5 pl-1">
                        {(group.options || []).map((opt, oi) => (
                          <div key={oi} className="space-y-1.5">
                            <div className="flex gap-2 items-center">
                              <Input
                                value={opt.name}
                                onChange={e => setProdForm(f => { const og = [...f.option_groups]; const ops = [...og[gi].options]; ops[oi] = { ...ops[oi], name: e.target.value }; og[gi] = { ...og[gi], options: ops }; return { ...f, option_groups: og }; })}
                                placeholder="Val (t.ex. Friterad)"
                                className="bg-[#121212] border-[#27272A] text-[#EDEDED] flex-1 h-8 text-sm"
                              />
                              <div className="flex items-center gap-1">
                                <span className="text-[#A8ABB2] text-xs">+</span>
                                <Input
                                  type="number"
                                  value={opt.price}
                                  onChange={e => setProdForm(f => { const og = [...f.option_groups]; const ops = [...og[gi].options]; ops[oi] = { ...ops[oi], price: e.target.value }; og[gi] = { ...og[gi], options: ops }; return { ...f, option_groups: og }; })}
                                  placeholder="0"
                                  className="bg-[#121212] border-[#27272A] text-[#EDEDED] w-16 h-8 text-sm"
                                />
                                <span className="text-[#A8ABB2] text-xs">kr</span>
                              </div>
                              <button type="button" onClick={() => setProdForm(f => { const og = [...f.option_groups]; og[gi] = { ...og[gi], options: og[gi].options.filter((_, i) => i !== oi) }; return { ...f, option_groups: og }; })} className="text-[#A8ABB2] hover:text-[#EF4444]">
                                <X className="w-3.5 h-3.5" />
                              </button>
                            </div>
                            <div className="pl-1">
                              <p className="text-[10px] text-[#A8ABB2] mb-1">Allergener för "{opt.name || "detta val"}"</p>
                              <AllergenPicker
                                value={opt.allergens}
                                onChange={(next) => setProdForm(f => { const og = [...f.option_groups]; const ops = [...og[gi].options]; ops[oi] = { ...ops[oi], allergens: next }; og[gi] = { ...og[gi], options: ops }; return { ...f, option_groups: og }; })}
                              />
                            </div>
                          </div>
                        ))}
                        <button type="button" onClick={() => setProdForm(f => { const og = [...f.option_groups]; og[gi] = { ...og[gi], options: [...(og[gi].options || []), { name: "", price: "" }] }; return { ...f, option_groups: og }; })} className="text-[#D4B483] hover:text-[#E5CFA5] text-xs font-medium mt-1">
                          + Lägg till val
                        </button>
                      </div>
                    </div>
                  ))}
                  <Button type="button" onClick={() => setProdForm(f => ({ ...f, option_groups: [...(f.option_groups || []), { name: "", options: [{ name: "", price: "" }] }] }))} size="sm" variant="outline" className="border-[#27272A] text-[#E8E8EA] hover:bg-[#1A1A1A] h-8 text-xs" data-testid="add-optgroup-btn">
                    <Plus className="w-3.5 h-3.5 mr-1.5" /> Lägg till alternativgrupp
                  </Button>
                </div>
              </div>

              <div>
                <Label className="text-[#E8E8EA] text-xs mb-1.5 block">
                  Recept / lagerförbrukning <span className="text-[#A8ABB2] normal-case">(råvaror som dras från lagret vid varje beställning)</span>
                </Label>
                <div className="space-y-2">
                  {/* Snabbval: klicka på en befintlig lagervara för att lägga till den utan att skriva (typo-säkert). */}
                  {(inventoryItems || []).length > 0 && (
                    <div className="bg-[#0A0A0A] border border-[#27272A] rounded-lg p-2.5 mb-1">
                      <p className="text-[10px] uppercase tracking-wider text-[#A8ABB2] mb-1.5">Finns i lager – klicka för att lägga till</p>
                      <div className="flex flex-wrap gap-1.5">
                        {inventoryItems.map(it => {
                          const inRecipe = (prodForm.recipe || []).some(r => r.item_id === it.id);
                          return (
                            <button
                              type="button"
                              key={it.id}
                              disabled={inRecipe}
                              onClick={() => setProdForm(f => ({ ...f, recipe: [...(f.recipe || []), { item_id: it.id, name: it.name, quantity: "", unit: it.unit || "st" }] }))}
                              className={`text-xs px-2.5 py-1 rounded-full border transition-all ${inRecipe ? "bg-[#1A1A1A] border-[#27272A] text-[#3F3F46] cursor-default" : "bg-[#121212] border-[#D4B483]/30 text-[#D4B483] hover:bg-[#D4B483]/10"}`}
                            >
                              {it.name}{inRecipe ? " ✓" : ""}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {(prodForm.recipe || []).map((row, ri) => {
                    const match = (inventoryItems || []).find(it => (it.name || "").trim().toLowerCase() === (row.name || "").trim().toLowerCase());
                    const linked = !!(row.item_id || match);
                    return (
                    <div key={ri} className="flex gap-2 items-center">
                      <div className="flex-1 relative">
                        <Input
                          value={row.name}
                          onChange={e => setProdForm(f => {
                            const val = e.target.value;
                            const m = (inventoryItems || []).find(it => (it.name || "").trim().toLowerCase() === val.trim().toLowerCase());
                            const rc = [...f.recipe];
                            rc[ri] = { ...rc[ri], name: val, item_id: m ? m.id : null, unit: m ? (m.unit || rc[ri].unit) : rc[ri].unit };
                            return { ...f, recipe: rc };
                          })}
                          placeholder="Råvara (t.ex. Burgarbröd)"
                          className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED] h-9 text-sm pr-16"
                          list="lager-items"
                        />
                        {row.name && (
                          <span className={`absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-semibold ${linked ? "text-[#10B981]" : "text-[#D4B483]"}`}>
                            {linked ? "i lager" : "ny"}
                          </span>
                        )}
                      </div>
                      <Input
                        type="number" step="any"
                        value={row.quantity}
                        onChange={e => setProdForm(f => { const rc = [...f.recipe]; rc[ri] = { ...rc[ri], quantity: e.target.value }; return { ...f, recipe: rc }; })}
                        placeholder="Antal"
                        className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED] w-20 h-9 text-sm"
                      />
                      {linked ? (
                        <span title="Enheten styrs av lagervaran" className="w-16 h-9 flex items-center justify-center text-sm text-[#10B981] bg-[#0A0A0A] border border-[#27272A] rounded-md">
                          {normUnit(match?.unit || row.unit)}
                        </span>
                      ) : (
                        <select
                          value={normUnit(row.unit)}
                          onChange={e => setProdForm(f => { const rc = [...f.recipe]; rc[ri] = { ...rc[ri], unit: e.target.value }; return { ...f, recipe: rc }; })}
                          className="bg-[#0A0A0A] border border-[#27272A] text-[#EDEDED] w-16 h-9 text-sm rounded-md px-1"
                        >
                          {UNIT_OPTIONS.map(u => <option key={u.value} value={u.value}>{u.value}</option>)}
                        </select>
                      )}
                      <button type="button" onClick={() => setProdForm(f => ({ ...f, recipe: f.recipe.filter((_, i) => i !== ri) }))} className="text-[#A8ABB2] hover:text-[#EF4444]">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    );
                  })}
                  <datalist id="lager-items">
                    {(inventoryItems || []).map(it => <option key={it.id} value={it.name} />)}
                  </datalist>
                  <button type="button" onClick={() => setProdForm(f => ({ ...f, recipe: [...(f.recipe || []), { item_id: null, name: "", quantity: "", unit: "st" }] }))} className="text-[#D4B483] hover:text-[#E5CFA5] text-xs font-medium">
                    + Lägg till ny råvara
                  </button>
                  <p className="text-[#A8ABB2] text-[11px]">Klicka på en vara ovan om den redan finns i lager. Nya råvaror läggs till automatiskt. Lagret minskar när en order accepteras.</p>
                </div>
              </div>

              <div>
                <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Tillval / Tillägg</Label>
                <div className="flex gap-2 mb-2">
                  <Input value={newMod.name} onChange={e => setNewMod(m => ({ ...m, name: e.target.value }))} placeholder="Namn" className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED] flex-1" data-testid="mod-name-input" />
                  <Input type="number" value={newMod.price} onChange={e => setNewMod(m => ({ ...m, price: e.target.value }))} placeholder="Pris" className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED] w-20" data-testid="mod-price-input" />
                  <Button type="button" onClick={addModifier} size="sm" className="bg-[#27272A] text-[#EDEDED] hover:bg-[#3F3F46]" data-testid="add-mod-btn">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="space-y-1.5">
                  {prodForm.modifiers.map((mod, i) => (
                    <div key={i} className="bg-[#0A0A0A] rounded-lg p-2.5 border border-[#27272A]">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-[#EDEDED]">{mod.name} <span className="text-[#D4B483]">+{mod.price} kr</span></span>
                        <button onClick={() => setProdForm(f => ({ ...f, modifiers: f.modifiers.filter((_, idx) => idx !== i) }))} className="text-[#E8E8EA] hover:text-[#EF4444]">
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                      <div className="mt-2">
                        <p className="text-[10px] text-[#A8ABB2] mb-1">Allergener</p>
                        <AllergenPicker
                          value={mod.allergens}
                          onChange={(next) => setProdForm(f => ({ ...f, modifiers: f.modifiers.map((m, idx) => idx === i ? { ...m, allergens: next } : m) }))}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <Button onClick={handleSaveProduct} disabled={saving} className="w-full bg-[#D4B483] text-[#0A0A0A] hover:bg-[#E5CFA5] h-11 font-semibold" data-testid="save-prod-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingProduct ? "Update Product" : "Save Product")}
              </Button>
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
}
